import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { PROJECTS_DOWNLOAD_FIELDS } from 'utils/constants';

import { streamDownload } from 'api/projects';
import Clickwrap from 'components/Clickwrap';
import { updateProjectsDownloadPreferences } from 'apollo/projects.query';
import APModal from 'components/Modal/APModal';
import ErrorModal from 'components/Modal/ConfirmationModal';

import './styles.scss';

/**
 * @description: Download Modal Component from ActionButton.
 * @param {*}
 * @return {*}
 */
export const ProjectsDownloadModal = ({
  dismiss,
  downloadIsOpen,
  setDownloadIsOpen,
}) => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const viewOptions = useSelector(
    (state) => state.global.projectsDownloadViewOptions,
  );
  const { orgId } = userInfo;

  const clickwrapColor = '#3d3d3d';

  const [downloadViewOptions, setDownloadViewOptions] = useState([]);
  const showDownloadPDF = false;

  useEffect(() => {
    // Looks at visible fields to auto-select download fields.
    setDownloadViewOptions(
      PROJECTS_DOWNLOAD_FIELDS.map((field) => {
        const checked =
          viewOptions.find((option) => field.id === option.id)?.checked ??
          false;

        return {
          ...field,
          checked,
        };
      }),
    );
  }, [viewOptions]);

  const updatePreferences = async (options) => {
    const data = await updateProjectsDownloadPreferences(options);
    return data;
  };

  // Download radio states
  const [downloadStatus, setDownloadStatus] = useState('all');
  const [downloadFields, setDownloadFields] = useState('all');

  // Error state
  const [hasError, setHasError] = useState(false);

  // Preparing status
  const [isPreparingCSV, setIsPreparingCSV] = useState(false);
  const [isPreparingPDF, setIsPreparingPDF] = useState(false);

  const downloadCSV = () => {
    const download_type = 'org.weddings-csv';
    const params = {};
    params.status_label = downloadStatus;

    if (downloadFields === 'customize') {
      const fieldsParam = downloadViewOptions
        .filter((field) => field.checked)
        .reduce((prev, field) => prev.concat(field.param), []);

      if (fieldsParam.length) {
        params.fields = fieldsParam;
      }
    }

    setIsPreparingCSV(true);

    streamDownload({ orgId, download_type, params })
      .then((response) => {
        if(response.status !== 401) window.location.replace(response.data.url);
        setIsPreparingCSV(false);
        dismiss();
        setDownloadIsOpen(false);
      })
      .catch(() => {
        setIsPreparingCSV(false);
        setHasError(true);
      });
  };

  const downloadPDF = () => {
    const download_type = 'org.weddings';
    const params = {};
    params.status_label = downloadStatus;

    if (downloadFields === 'customize') {
      const fieldsParam = downloadViewOptions
        .filter((field) => field.checked)
        .reduce((prev, field) => prev.concat(field.param), []);

      if (fieldsParam.length) {
        params.fields = fieldsParam;
      }
    }

    setIsPreparingPDF(true);

    streamDownload({ orgId, download_type, params })
      .then((response) => {
        if(response.status !== 401) window.location.replace(response.data.url);
        setIsPreparingPDF(false);
        dismiss();
        setDownloadIsOpen(false);
      })
      .catch(() => {
        setIsPreparingPDF(false);
        setHasError(true);
      });
  };

  const errorCloseHandler = () => {
    setHasError(false);
  };

  const DownloadOptions = () => {
    const secondColumnStart = Math.ceil(downloadViewOptions.length / 2);

    const onDownloadCheckChange = (index) => {
      const newDownloadOptions = [...downloadViewOptions];
      const { checked } = downloadViewOptions[index];
      newDownloadOptions[index].checked = !checked;
      setDownloadViewOptions(newDownloadOptions);
      updatePreferences(newDownloadOptions);
    };

    return (
      <div className="download-options form-group text-left">
        <div className="download-options__field">
          <label className="field-title">Select</label>
          <div className="d-flex">
            <div className="form-check form-check--inline">
              <input
                id="status-all-radio"
                name="status-all"
                type="radio"
                value="all"
                checked={downloadStatus === 'all'}
                onChange={() => {
                  setDownloadStatus('all');
                }}
              />
              <label
                htmlFor="status-all-radio"
                className="form-check-label"
              >
                All
              </label>
            </div>
            <div className="form-check form-check--inline">
              <input
                id="status-active-radio"
                name="status-active"
                type="radio"
                value="active"
                checked={downloadStatus === 'active'}
                onChange={() => {
                  setDownloadStatus('active');
                }}
              />
              <label htmlFor="status-active-radio"
                className="form-check-label"
              >
                Active only
              </label>
            </div>
            <div className="form-check form-check--inline">
              <input
                id="status-archive-radio"
                name="status-archive"
                type="radio"
                value="archived"
                checked={downloadStatus === 'archive'}
                onChange={() => {
                  setDownloadStatus('archive');
                }}
              />
              <label
                htmlFor="status-archive-radio"
                className="form-check-label"
              >
                Archive only
              </label>
            </div>
          </div>
        </div>
        <div className="download-options__field">
          <label className="field-title">Fields</label>
          <div className="d-flex">
            <div className="form-check form-check--inline">
              <input
                id="fields-all-radio"
                name="fields-all"
                type="radio"
                value="fields-all"
                checked={downloadFields === 'all'}
                onChange={() => {
                  setDownloadFields('all');
                }}
              />
              <label htmlFor="fields-all-radio"
                className="form-check-label"
              >
                All
              </label>
            </div>
            <div className="form-check form-check--inline">
              <input
                id="fields-customize-radio"
                name="customize"
                type="radio"
                value="customize"
                checked={downloadFields === 'customize'}
                onChange={() => {
                  setDownloadFields('customize');
                }}
              />
              <label
                htmlFor="fields-customize-radio"
                className="form-check-label"
              >
                Customize
              </label>
            </div>
          </div>
        </div>
        {downloadFields === 'customize' && (
          <div className="download-parameters download-parameters--fields">
            <div className="download-parameters__column">
              {downloadViewOptions
                .slice(0, secondColumnStart)
                .map((option, key) => {
                  return (
                    <div
                      key={option.id}
                      className="download-parameter view-option view-option--download"
                    >
                      <Clickwrap
                        name={`download-field--${option.id}`}
                        checked={option.checked}
                        className="clickwrap--view-option download-parameter__checkbox small"
                        onChange={() => onDownloadCheckChange(key)}
                        color={clickwrapColor}
                      />
                      <label htmlFor={`download-field--${option.id}`}>
                        {option.name}
                      </label>
                    </div>
                  );
                })}
            </div>
            <div className="download-parameters__column">
              {downloadViewOptions
                .slice(secondColumnStart)
                .map((option, key) => {
                  return (
                    <div
                      key={option.id}
                      className="download-parameter view-option view-option--download"
                    >
                      <Clickwrap
                        name={`download-field--${option.id}`}
                        checked={option.checked}
                        className="clickwrap--view-option download-parameter__checkbox small"
                        onChange={() =>
                          onDownloadCheckChange(secondColumnStart + key)
                        }
                        color={clickwrapColor}
                      />
                      <label htmlFor={`download-field--${option.id}`}>
                        {option.name}
                      </label>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const DownloadAction = () => {
    return (
      <>
        <button
          className={clsx('download-options__download button', {
            disabled: isPreparingCSV,
            preparing: isPreparingCSV,
          })}
          onClick={downloadCSV}
        >
          {!isPreparingCSV && <span>Download CSV</span>}
          {isPreparingCSV && (
            <>
              <span className="visually-hidden--mobile">Downloading</span>
              <span className="fa fa-spin fa-spinner" />
            </>
          )}
        </button>
        {showDownloadPDF && (
          <button
            className={clsx('download-options__download button', {
              disabled: isPreparingPDF,
              preparing: isPreparingPDF,
            })}
            onClick={downloadPDF}
          >
            {!isPreparingPDF && <span>Download PDF</span>}
            {isPreparingPDF && (
              <>
                <span className="visually-hidden--mobile">Downloading</span>
                <span className="fa fa-spin fa-spinner" />
              </>
            )}
          </button>
        )}
      </>
    );
  };

  return (
    <>
      <APModal
        open={downloadIsOpen}
        onClose={() => setDownloadIsOpen(false)}
        name="projects-download"
        title="Download"
        actions={DownloadAction()}
        size="narrow"
      >
        {DownloadOptions()}
      </APModal>
      {hasError && (
        <ErrorModal
          visible={hasError}
          onClose={errorCloseHandler}
          actions={(
            <button
              onClick={errorCloseHandler}
              className="button"
            >
              Close
            </button>
          )}
        >
          <p>Sorry, we couldn't prepare the download. Please try again.</p>
        </ErrorModal>
      )}
    </>
  );
};
